import React, {useContext} from "react";
import {useAsync2} from "nate-react-api-helpers";
import {useNavigate} from "react-router-dom";
import {SelectEditor} from "../../../../misc/scroller/SelectEditor";
import {EditContext} from "../../../../misc/scroller/EditProvider";
import {api} from "../../../../api/API";
import {adoptHardwareElevation} from "./adoptHardwareElevation";
import {useConfirm} from "../../../../misc/Confirm";
import {useProjectId} from "../ProjectName";
import {useSnackAction} from "../hardware/SnackAction";
import {DropdownDataLoading} from "../../settings/DropDowns";

// the in-cell picker shown for an opening's hardware elevation: an autocomplete of the
// project's existing hardware elevations. Selecting one links this opening to that hardware
// (see drw.AdoptHardwareElevationHandler — no identity change, like the door picker), then
// reloads the row. Typing a name and picking the free-solo entry instead unlinks the opening
// and opens it in the elevation tool to draw a fresh one.
export function PickHardwareElevation(props: {
    anchor: any;
    row: {id: number};
    width: number;
    onCancel(): void;
}) {
    const project = useProjectId();
    const editCtx = useContext(EditContext);
    const confirm = useConfirm();
    const navigate = useNavigate();

    const list = useAsync2((p: number) => api.elevationTool.listHardwareElevations({project: p}), project, [project]);

    const adopt = useSnackAction(async (hardware: number) => {
        await adoptHardwareElevation(confirm.confirm, {project, opening: props.row.id, hardware});
        await editCtx.reload(true, [props.row.id]);
        props.onCancel();
    }, {success: "Hardware elevation linked"});

    // free-solo: unlink this opening's hardware elevation, then open the tool, passing the
    // typed name so it auto-saves a fresh hardware elevation under it.
    const drawNew = useSnackAction(async (name: string) => {
        await api.elevationTool.unlinkHardware({project, opening: props.row.id});
        navigate(`/project/${project}/shop-drawing/elevation-tool?opening=${props.row.id}&workspace=hardware&autoSaveName=${encodeURIComponent(name.trim())}`);
    }, {success: "Unlinked — opening elevation tool"});

    if(list.loading) return <DropdownDataLoading anchor={props.anchor} width={props.width} />;

    const options = list.asList.map(s => ({value: s.id.toString(), display: s.name}));

    return (
        <SelectEditor
            anchor={props.anchor}
            options={options}
            value=""
            initialValue=""
            freeSolo
            onBlur={props.onCancel}
            onSelect={(value, freeSolo) => freeSolo ? drawNew.callback(value) : adopt.callback(parseInt(value))}
        />
    );
}
